import React, { useState, useEffect, useRef } from 'react';
import { Icons } from '../Icons';
import { DataSourceBadge } from '../ui/DataSourceBadge';
import { useLanguage } from '../../contexts/LanguageContext';

const getNavSections = (t) => [
  { id: 'prehled', label: t('subnav.sections.overview') },
  { id: 'vitals', label: t('subnav.sections.vitals') },
  { id: 'dalsi', label: t('subnav.sections.other') },
  { id: 'zarizeni', label: t('subnav.sections.devices') },
  { id: 'server', label: t('subnav.sections.server') },
  { id: 'historie', label: t('subnav.sections.history') }
];

const getDeviceOptions = (t) => [
  { value: 'ALL', label: t('subnav.deviceOptions.all') },
  { value: 'PHONE', label: t('subnav.deviceOptions.phone') },
  { value: 'TABLET', label: t('subnav.deviceOptions.tablet') },
  { value: 'DESKTOP', label: t('subnav.deviceOptions.desktop') }
];

const Dropdown = ({ options, value, onChange, label, icon }) => {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef(null);
  
  const currentOption = options.find(opt => opt.value === value || opt.id === value);
  
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);
  
  return (
    <div ref={dropdownRef} className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="flex items-center gap-2 px-3 py-1.5 bg-slate-800/80 hover:bg-slate-700/80 border border-slate-600/50 hover:border-cyan-500/50 rounded-lg text-sm transition-all"
      >
        {icon}
        <span className="text-slate-200">{currentOption?.label || label}</span>
        <svg className={`w-4 h-4 text-slate-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
        </svg>
      </button>
      
      {isOpen && (
        <div className="absolute top-full left-0 mt-1 min-w-[160px] bg-slate-800 border border-slate-600/50 rounded-lg shadow-xl z-50 py-1 backdrop-blur-xl">
          {options.map(option => (
            <button
              key={option.value || option.id}
              onClick={() => {
                onChange(option.value || option.id);
                setIsOpen(false);
              }}
              className={`w-full text-left px-3 py-2 text-sm hover:bg-slate-700/80 transition-colors ${
                (option.value === value || option.id === value) ? 'text-cyan-400 bg-slate-700/50' : 'text-slate-300'
              }`}
            >
              {option.label}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

export const StickySubNav = ({ domain, selectedDevice, onDeviceChange, visible, usedTarget, isLoggedIn = false, onShare, onOpenMonitoring, onOpenLabTest, onSaveAnalysis, savingAnalysis = false, savedAnalysis = false, dataSource = 'crux', fromCache = false, tierSlug = null, viewMode = 'detailed', onViewModeChange, isLabMode = false }) => {
  const { t } = useLanguage();
  const [activeSection, setActiveSection] = useState('prehled');
  const isPageSpeedMode = dataSource === 'pagespeed' || dataSource === 'lighthouse';
  const NAV_SECTIONS = getNavSections(t);
  const DEVICE_OPTIONS = getDeviceOptions(t);
  
  const getEffectiveSource = () => {
    if (fromCache) return 'cache';
    if (isPageSpeedMode) return 'pagespeed';
    if (dataSource === 'bigquery' || dataSource === 'bigquery_monthly') return 'bigquery';
    return 'crux';
  };
  
  useEffect(() => {
    if (!visible) return;
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            const sectionId = entry.target.id.replace('section-', '');
            setActiveSection(sectionId);
          }
        });
      },
      { rootMargin: '-100px 0px -50% 0px', threshold: 0 }
    );
    
    NAV_SECTIONS.forEach(({ id }) => {
      const el = document.getElementById(`section-${id}`);
      if (el) observer.observe(el);
    });
    
    return () => observer.disconnect();
  }, [visible]);
  
  const scrollToSection = (id) => {
    setActiveSection(id);
    const el = document.getElementById(`section-${id}`);
    if (el) {
      const offset = 80;
      const elementPosition = el.getBoundingClientRect().top;
      const offsetPosition = elementPosition + window.pageYOffset - offset;
      window.scrollTo({ top: offsetPosition, behavior: 'smooth' });
    }
  };
  
  const sectionOptions = NAV_SECTIONS.map(s => ({ value: s.id, label: s.label }));
  
  return (
    <div className={`sticky-subnav ${visible ? 'visible' : ''} ${isLoggedIn ? 'logged-in' : ''}`}>
      <div className="sticky-subnav-inner">
        <div className="subnav-domain hidden md:flex items-center gap-3">
          <div className="flex items-center gap-1.5">
            <Icons.Globe size={14} className="text-cyan-400" />
            <span>{usedTarget || domain || t('subnav.analysis')}</span>
          </div>
          <DataSourceBadge source={getEffectiveSource()} size="xs" />
        </div>
        
        <div className="flex items-center gap-3 overflow-x-auto scrollbar-hide" style={{ WebkitOverflowScrolling: 'touch', scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
          {/* View Mode Toggle - Executive/Detailed - hide for Lab mode */}
          {!isLabMode && (
            <div className="inline-flex items-center bg-gradient-to-r from-cyan-500/30 to-purple-500/30 rounded-lg p-0.5 border-2 border-cyan-400 flex-shrink-0">
              <button
                type="button"
                onClick={() => onViewModeChange?.('executive')}
                className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all flex items-center gap-1.5 ${
                  viewMode === 'executive'
                    ? 'bg-cyan-500 text-white shadow-lg'
                    : 'text-white hover:bg-cyan-500/50'
                }`}
              >
                <Icons.Chart size={14} />
                Executive
              </button>
              <button
                type="button"
                onClick={() => onViewModeChange?.('detailed')}
                className={`px-3 py-1.5 rounded-md text-xs font-bold transition-all flex items-center gap-1.5 ${
                  viewMode === 'detailed'
                    ? 'bg-purple-500 text-white shadow-lg'
                    : 'text-white hover:bg-purple-500/50'
                }`}
              >
                <Icons.Code size={14} />
                {t('subnav.detailed')}
              </button>
            </div>
          )}
          
          {/* Section anchors - hide for Lab mode */}
          {!isLabMode && (
            <div className="flex-shrink-0">
              <Dropdown
                options={sectionOptions}
                value={activeSection}
                onChange={scrollToSection}
                label={t('subnav.section')}
                icon={<Icons.Layers size={14} className="text-cyan-400" />}
              />
            </div>
          )}
          
          <button
            onClick={onShare}
            className="flex items-center gap-2 px-3 py-1.5 bg-gradient-to-r from-cyan-500/20 to-purple-500/20 hover:from-cyan-500/30 hover:to-purple-500/30 border border-cyan-500/50 hover:border-cyan-400 rounded-lg text-sm transition-all flex-shrink-0"
          >
            <Icons.Share size={14} className="text-cyan-400" />
            <span className="text-cyan-300 hidden sm:inline">{t('subnav.share') || 'Sdílet'}</span>
          </button>
          
          {isLoggedIn && onSaveAnalysis && (
            <button
              onClick={onSaveAnalysis}
              disabled={savingAnalysis || savedAnalysis}
              className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all flex-shrink-0 disabled:opacity-70 ${
                savedAnalysis
                  ? 'bg-green-500/20 border border-green-500/50'
                  : 'bg-gradient-to-r from-green-500/20 to-emerald-500/20 hover:from-green-500/30 hover:to-emerald-500/30 border border-green-500/50 hover:border-green-400'
              }`}
              title={t('subnav.saveAnalysis') || 'Uložit analýzu'}
            >
              {savedAnalysis ? (
                <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
              ) : savingAnalysis ? (
                <svg className="w-4 h-4 text-green-400 animate-spin" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
              ) : (
                <svg className="w-4 h-4 text-green-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7H5a2 2 0 00-2 2v9a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2h-3m-1 4l-3 3m0 0l-3-3m3 3V4" />
                </svg>
              )}
              <span className={`hidden sm:inline ${savedAnalysis ? 'text-green-300' : 'text-green-300'}`}>
                {savedAnalysis ? (t('subnav.saved') || 'Uloženo ✓') : savingAnalysis ? (t('subnav.saving') || 'Ukládám...') : (t('subnav.save') || 'Uložit')}
              </span>
            </button>
          )}

          {isLoggedIn && (
            <>
              <button
                onClick={onOpenMonitoring}
                className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all flex-shrink-0 ${
                  ['premium', 'enterprise'].includes(tierSlug)
                    ? 'bg-gradient-to-r from-purple-500/20 to-pink-500/20 hover:from-purple-500/30 hover:to-pink-500/30 border border-purple-500/50 hover:border-purple-400'
                    : 'bg-slate-800/60 border border-slate-600/50 hover:border-slate-500'
                }`}
                title={['premium', 'enterprise'].includes(tierSlug) ? "Nastavit upozorneni na zmeny vykonnosti" : "Vyzaduje Premium tarif"}
              >
                <svg className={`w-4 h-4 ${['premium', 'enterprise'].includes(tierSlug) ? 'text-purple-400' : 'text-slate-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                </svg>
                <span className={`hidden sm:inline ${['premium', 'enterprise'].includes(tierSlug) ? 'text-purple-300' : 'text-slate-400'}`}>Monitoring</span>
                {!['premium', 'enterprise'].includes(tierSlug) && (
                  <svg className="w-3 h-3 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                )}
              </button>
              
              {/* Hide Lab Test button when already in Lab mode */}
              {!isLabMode && (
                <button
                  onClick={onOpenLabTest}
                  className={`flex items-center gap-2 px-3 py-1.5 rounded-lg text-sm transition-all flex-shrink-0 ${
                    ['premium', 'enterprise'].includes(tierSlug)
                      ? 'bg-gradient-to-r from-cyan-500/20 to-blue-500/20 hover:from-cyan-500/30 hover:to-blue-500/30 border border-cyan-500/50 hover:border-cyan-400'
                      : 'bg-slate-800/60 border border-slate-600/50 hover:border-slate-500'
                  }`}
                  title={['premium', 'enterprise'].includes(tierSlug) ? "Synteticky test vykonnosti" : "Vyzaduje Premium tarif"}
                >
                  <svg className={`w-4 h-4 ${['premium', 'enterprise'].includes(tierSlug) ? 'text-cyan-400' : 'text-slate-400'}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                  </svg>
                  <span className={`hidden lg:inline ${['premium', 'enterprise'].includes(tierSlug) ? 'text-cyan-300' : 'text-slate-400'}`}>Lab Test</span>
                  {!['premium', 'enterprise'].includes(tierSlug) && (
                    <svg className="w-3 h-3 text-amber-400" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                    </svg>
                  )}
                </button>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default StickySubNav;
